import { Link } from 'react-router-dom';
import './FundFriendsPrivacy.css';

const EFFECTIVE_DATE = 'September 7, 2026';

// A numbered paragraph in the app's voice: the number sits quiet, the text does the work.
function P({ n, children }) {
  return (
    <p>
      {n ? <span className="n">{n}</span> : null}
      {children}
    </p>
  );
}

function FundFriendsPrivacy() {
  return (
    <div className="ff">
      <div className="ff-wrap">
        <header className="ff-header">
          <Link to="/" className="ff-brand">tnysocial</Link>
          <Link to="/" className="ff-pill ff-pill--grey">back</Link>
        </header>

        <div className="ff-icon" aria-hidden="true">
          <span>$</span>
          <span>$</span>
        </div>
        <h1 className="ff-hero">fund friends<br />privacy.</h1>
        <p className="ff-date">effective {EFFECTIVE_DATE}</p>

        <section className="ff-cell">
          <h2>1. who this covers</h2>
          <P n="1.1">
            This Privacy Policy governs the processing of Personal Information by tnysocial LLC, a limited liability
            company organised under the laws of the State of Delaware, United States of America (the "Company"), in
            connection with the mobile software application designated Fund Friends, comprising an iMessage extension
            and a companion application (the "Application").
          </P>
          <P n="1.2">
            The Company is the controller in respect of the processing described herein, and is the business to which
            requests under United States state privacy legislation are to be directed.
          </P>
          <P n="1.3">
            This Privacy Policy is incorporated into the <Link to="/terms">Terms and Conditions</Link> and forms part
            thereof. The Company's other applications are governed by the{' '}
            <Link to="/privacy">general Privacy Policy</Link>.
          </P>
          <P n="1.4">
            Requests, enquiries and notices under this Privacy Policy are to be directed to{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>.
          </P>
        </section>

        <section className="ff-cell">
          <h2>2. what the app does</h2>
          <P n="2.1">
            Fund Friends permits a User to ask the participants of an iMessage conversation to contribute money
            towards a stated purpose, and permits those participants to record that they have contributed.
          </P>
          <P n="2.2">
            The Application does not process payments. Contributions are made outside the Application by means of the
            Cash App or Venmo application of the User's choosing, and are governed by the terms and privacy policy of
            Block, Inc. or PayPal, Inc. respectively. The Company receives no payment information and cannot verify
            whether any contribution was made. A contribution recorded within the Application is a statement by the
            contributing User and nothing more.
          </P>
        </section>

        <section className="ff-cell">
          <h2>3. no accounts</h2>
          <P n="3.1">
            The Application does not provide for registered accounts and does not employ Sign in with Apple or any
            other identity provider. The Company does not collect any electronic mail address, telephone number,
            postal address, date of birth or payment card number.
          </P>
          <P n="3.2">
            Authentication is anonymous. Upon first launch a randomly generated identifier (the "Anonymous
            Identifier") is provisioned by Firebase Authentication. It is not derived from the User, the device, or
            any account of the User with any third party, and is not used by the Company to identify any natural
            person.
          </P>
        </section>

        <section className="ff-cell">
          <h2>4. what we keep</h2>
          <P n="4.1">The following are provided by the User and are processed by the Company:</P>
          <ul>
            <li>a display name, being whatever the User elects to type, shown beside the User's asks and contributions;</li>
            <li>at the User's election, a photograph selected from the User's photo library, reduced to an avatar of
              not more than 160 pixels square, shown in place of the User's initial;</li>
            <li>the User's Cash App cashtag and/or Venmo username, shown to participants so that they may contribute;</li>
            <li>the content of each ask, comprising a short description, an amount, an emoji, a colour and a closing
              time; and</li>
            <li>the amount the User states to have contributed to another User's ask.</li>
          </ul>
          <P n="4.2">
            The Anonymous Identifier is processed automatically to associate the foregoing with the device. Internet
            protocol addresses are received by the Company's Processor as an incident of network communication.
          </P>
          <P n="4.3">
            The Application processes crash reports and a small number of usage events (for example, that an ask was
            sent or a contribution recorded, with the amount) by means of Firebase Crashlytics and Google Analytics
            for Firebase, associated with a random installation identifier and not with the User's name, handles or
            any direct identifier. Such data is used solely to find defects and understand how the Application is
            used, never for advertising, and is retained for the standard periods of those services.
          </P>
          <P n="4.4">
            The Application collects no session recordings, advertising identifiers, precise location, contacts,
            calendar entries, camera or microphone input. It displays no advertising and contains no subscription.
          </P>
        </section>

        <section className="ff-cell">
          <h2>5. messages</h2>
          <P n="5.1">
            Each ask is also carried within the iMessage message itself, so that the conversation continues to
            display it without the Company's involvement. Such messages are transmitted and stored by Apple Inc.
            under the terms of iMessage and are outside the Company's control.
          </P>
          <P n="5.2">
            The Company does not receive the identity, telephone number, Apple ID or contact details of any
            participant in any conversation. Apple furnishes the Application with opaque participant identifiers
            only.
          </P>
        </section>

        <section className="ff-cell">
          <h2>6. who sees it</h2>
          <P n="6.1">
            The information at Section 4.1 is disclosed to the other participants of any conversation in which the
            User sends or contributes to an ask, and to any person with whom such a participant shares the
            conversation. A cashtag or username so disclosed may be used by any such person to send money to the
            User outside the Application.
          </P>
          <P n="6.2">
            The Company discloses Personal Information to the following Processors, each for the purpose stated and
            pursuant to contractual terms restricting its use: Google LLC, in respect of Firebase Authentication and
            Cloud Firestore, for anonymous authentication and for storage of the information at Article 4, and in
            respect of Firebase Crashlytics and Google Analytics for Firebase, for the crash reports and usage events
            at Section 4.3; and Apple Inc., in respect of distribution and iMessage.
          </P>
          <P n="6.3">
            The Company does not sell Personal Information, does not disclose it to data brokers, and does not
            otherwise disclose it save: where required by applicable law or valid legal process; where reasonably
            necessary to investigate suspected fraud or to establish, exercise or defend legal claims; where
            reasonably necessary to protect the rights, property or safety of the Company, its Users or the public;
            and to an acquirer or successor in connection with any merger, acquisition, reorganisation or sale of
            assets, in which event notice shall be given by revision of this page prior to any such transfer.
          </P>
        </section>

        <section className="ff-cell">
          <h2>7. why we process it</h2>
          <P n="7.1">
            The information at Article 4 is processed for the provision and operation of the Application and for
            compliance with legal obligations to which the Company is subject. Where the UK GDPR or the EU GDPR
            applies, the Company relies upon performance of a contract in respect of the operation of the
            Application.
          </P>
          <P n="7.2">
            The Company does not take decisions based solely upon automated processing which produce legal effects
            concerning the User or which similarly significantly affect the User.
          </P>
        </section>

        <section className="ff-cell">
          <h2>8. deleting it</h2>
          <P n="8.1">
            The information at Section 4.1 is retained until erased by the User. The Application provides an erasure
            facility within its settings, designated "delete my data", which deletes the anonymous authentication
            identity and the User's display name, avatar and payout handles held by the Company, and erases the
            information retained locally upon the device.
          </P>
          <P n="8.2">
            Asks the User has sent continue to display within the conversations in which they were sent, under the
            control of Apple Inc. and the participants, and continue to be held by the Company so that those
            conversations continue to function. Erasure of a particular ask is to be requested at{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>.
          </P>
          <P n="8.3">
            Information retained locally upon the device, comprising the User's name, handles, avatar and the list of
            asks sent and opened, is erased upon deletion of the Application.
          </P>
        </section>

        <section className="ff-cell">
          <h2>9. security</h2>
          <P n="9.1">
            The Company implements technical and organisational measures appropriate to the nature and volume of the
            Personal Information processed, including encryption in transit and database access rules confining each
            User to writing their own display name, avatar and payout handles, their own asks, and their own
            contributions to the asks of others.
          </P>
          <P n="9.2">
            Information at Section 4.1 is readable by authenticated instances of the Application, which is necessary
            for it to be shown within conversations. It is not searchable by any User and is not publicly accessible.
            No method of transmission or storage is entirely secure, and the Company does not warrant absolute
            security.
          </P>
        </section>

        <section className="ff-cell">
          <h2>10. minors</h2>
          <P n="10.1">
            The Application is intended for a general audience aged thirteen (13) years and above. It is not directed
            to children under the age of thirteen (13) years, and the Company does not knowingly collect Personal
            Information from such children. In certain jurisdictions a higher minimum age applies, being sixteen (16)
            years in certain Member States of the European Union.
          </P>
          <P n="10.2">
            A parent or legal guardian who believes that a child under the age of thirteen (13) years has provided
            Personal Information to the Company may notify{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>, whereupon such information shall be erased.
          </P>
        </section>

        <section className="ff-cell">
          <h2>11. your rights in the united states</h2>
          <P n="11.1">
            Residents of California, Colorado, Connecticut, Virginia and other states having comprehensive privacy
            legislation are entitled, subject to the conditions and exceptions prescribed by the applicable statute,
            to know the categories and specific items of Personal Information processed; to obtain a copy thereof;
            to require correction of inaccuracies; to require deletion; and not to be subjected to discriminatory
            treatment by reason of the exercise of any such right.
          </P>
          <P n="11.2">
            The Company does not sell or share Personal Information within the meaning of the California Consumer
            Privacy Act and has not done so.
          </P>
          <P n="11.3">
            Requests are to be delivered to <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a> and shall be
            responded to within forty-five (45) days, subject to any permitted extension. The Company holds no
            electronic mail address or other direct identifier by reference to which a requesting person may be
            verified, and may accordingly require the display name and cashtag or username recorded in the
            Application in order to locate the relevant records. An authorised agent may submit a request upon proof
            of authorisation.
          </P>
        </section>

        <section className="ff-cell">
          <h2>12. your rights in the uk and eea</h2>
          <P n="12.1">
            Data subjects in the United Kingdom and the European Economic Area are entitled, subject to the
            conditions and exceptions prescribed by the UK GDPR and the EU GDPR respectively, to access their
            personal data; to obtain rectification of inaccurate data; to obtain erasure; to obtain restriction of
            processing; to data portability; and to object to processing.
          </P>
          <P n="12.2">
            Requests are to be delivered to <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>. Section
            11.3 applies to the verification of such requests. A data subject is entitled to lodge a complaint with
            the supervisory authority of the Member State of habitual residence, place of work or place of the
            alleged infringement, and, in the United Kingdom, with the Information Commissioner's Office.
          </P>
        </section>

        <section className="ff-cell">
          <h2>13. where it goes</h2>
          <P n="13.1">
            The Company is established in the United States of America. Personal Information is processed in the
            United States of America and in such other territories as the Processors identified at Section 6.2 may
            operate.
          </P>
          <P n="13.2">
            Where Personal Information originating in the United Kingdom or the European Economic Area is transferred
            to a third country, the Company relies upon the transfer mechanisms incorporated in the data processing
            terms of the respective Processors, which mechanisms include the Standard Contractual Clauses approved by
            the European Commission and, where applicable, the United Kingdom International Data Transfer Addendum.
          </P>
        </section>

        <section className="ff-cell">
          <h2>14. changes</h2>
          <P n="14.1">
            The Company may amend this Privacy Policy from time to time. The Effective Date appearing above shall be
            revised upon any amendment. Where an amendment is material, including the introduction of a new category
            of Personal Information or a new purpose of processing, notice shall be given within the Application
            prior to the amendment taking effect.
          </P>
        </section>

        <section className="ff-cell">
          <h2>15. the company</h2>
          <P>
            tnysocial LLC, a Delaware limited liability company, State of Delaware, United States of America.{' '}
            <a href="mailto:hello@tnysocial.com">hello@tnysocial.com</a>
          </P>
        </section>

        <div className="ff-actions">
          <Link to="/" className="ff-pill">back to tnysocial</Link>
        </div>
        <p className="ff-foot">
          Cash App is a service of Block, Inc. Venmo is a service of PayPal, Inc. iMessage is a service of Apple Inc.
          Fund Friends is not affiliated with any of them.
        </p>
      </div>
    </div>
  );
}

export default FundFriendsPrivacy;
